import React, { useState, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { formatDate } from "../utils/formatDate";
import {
  FileJson,
  ChevronLeft,
  ChevronRight,
  Upload,
  FileText,
  Eye,
  Trash2,
  RefreshCw,
  X,
  File,
  CheckCircle,
  AlertCircle,
  Clock,
} from "lucide-react";
import { cocbGetWorkOrders, cocbGetStats, cocbExtractDocument, cocbDeleteWorkOrder } from "../api";
import "./COCBWorkOrdersList.css";

// Custom Dropdown Component for Status Filter
const CustomStatusDropdown = ({ value, onChange }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);

  const options = [
    { value: "all", label: "All Status" },
    { value: "pending", label: "Pending" },
    { value: "processing", label: "Processing" },
    { value: "completed", label: "Completed" },
    { value: "failed", label: "Failed" },
  ];

  const selectedOption = options.find((opt) => opt.value === value);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener("mousedown", handleClickOutside);
      return () => document.removeEventListener("mousedown", handleClickOutside);
    }
  }, [isOpen]);

  return (
    <div className="cocb-custom-dropdown" ref={dropdownRef}>
      <button className="cocb-dropdown-trigger" onClick={() => setIsOpen(!isOpen)}>
        <span>{selectedOption?.label}</span>
        <svg className={`dropdown-arrow ${isOpen ? "open" : ""}`} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <polyline points="6 9 12 15 18 9"></polyline>
        </svg>
      </button>
      {isOpen && (
        <div className="cocb-dropdown-menu">
          {options.map((option) => (
            <button
              key={option.value}
              className={`cocb-dropdown-option ${value === option.value ? "selected" : ""}`}
              onClick={() => { onChange(option.value); setIsOpen(false); }}
            >
              {option.label}
              {value === option.value && <span className="check-mark">✓</span>}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

// Upload Modal Component
const UploadModal = ({ isOpen, onClose, onUploadComplete }) => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [uploadStatus, setUploadStatus] = useState({});
  const [uploadResults, setUploadResults] = useState([]);
  const [uid, setUid] = useState("");
  const fileInputRef = useRef(null);
  const dropZoneRef = useRef(null);

  const handleFileSelect = (event) => {
    const selectedFiles = Array.from(event.target.files || []);
    addFiles(selectedFiles);
  };

  const addFiles = (newFiles) => {
    const validFiles = newFiles.filter(f => 
      f.name.toLowerCase().endsWith('.pdf') || 
      f.name.toLowerCase().endsWith('.tiff') ||
      f.name.toLowerCase().endsWith('.tif') ||
      f.name.toLowerCase().endsWith('.jpg') ||
      f.name.toLowerCase().endsWith('.jpeg') ||
      f.name.toLowerCase().endsWith('.png')
    );
    const existingNames = files.map(f => f.name);
    const uniqueFiles = validFiles.filter(f => !existingNames.includes(f.name));
    
    if (validFiles.length !== newFiles.length) {
      alert('Only PDF, TIFF, JPG, PNG files are supported. Other files were skipped.');
    }
    
    setFiles(prev => [...prev, ...uniqueFiles]);
    
    const newStatus = {};
    uniqueFiles.forEach(f => {
      newStatus[f.name] = 'pending';
    });
    setUploadStatus(prev => ({ ...prev, ...newStatus }));
  };

  const handleDrop = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
    
    const droppedFiles = Array.from(event.dataTransfer.files);
    addFiles(droppedFiles);
  };

  const handleDragOver = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.add('drag-over');
  };

  const handleDragLeave = (event) => {
    event.preventDefault();
    event.stopPropagation();
    dropZoneRef.current?.classList.remove('drag-over');
  };

  const removeFile = (filename) => {
    setFiles(prev => prev.filter(f => f.name !== filename));
    setUploadStatus(prev => {
      const newStatus = { ...prev };
      delete newStatus[filename];
      return newStatus;
    });
  };

  const handleUploadAll = async () => {
    if (files.length === 0) return;
    
    setUploading(true);
    const results = [];

    for (const file of files) {
      setUploadStatus(prev => ({ ...prev, [file.name]: 'uploading' }));
      
      try {
        const result = await cocbExtractDocument(file, uid || undefined);
        setUploadStatus(prev => ({ ...prev, [file.name]: 'success' }));
        results.push({ filename: file.name, success: true, work_order_id: result.work_order?.id });
      } catch (err) {
        setUploadStatus(prev => ({ ...prev, [file.name]: 'error' }));
        results.push({ filename: file.name, success: false, error: err.message });
      }
    }

    setUploadResults(results);
    setUploading(false);
    
    if (onUploadComplete) {
      onUploadComplete(results);
    }
  };

  const handleClose = () => {
    setFiles([]);
    setUploadStatus({});
    setUploadResults([]);
    setUid("");
    onClose();
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'uploading':
        return <RefreshCw size={16} className="spin" style={{ color: '#3b82f6' }} />;
      case 'success':
        return <CheckCircle size={16} style={{ color: '#22c55e' }} />;
      case 'error':
        return <AlertCircle size={16} style={{ color: '#ef4444' }} />;
      default:
        return <Clock size={16} style={{ color: '#9ca3af' }} />;
    }
  };

  if (!isOpen) return null;

  const allUploaded = files.length > 0 && files.every(f => uploadStatus[f.name] === 'success' || uploadStatus[f.name] === 'error');
  const successCount = Object.values(uploadStatus).filter(s => s === 'success').length;
  const errorCount = Object.values(uploadStatus).filter(s => s === 'error').length;

  return (
    <div className="cocb-modal-overlay" onClick={handleClose}>
      <div className="cocb-upload-modal" onClick={e => e.stopPropagation()}>
        <div className="cocb-modal-header">
          <h3>
            <Upload size={20} />
            Upload & Extract - COCB
          </h3>
          <button className="cocb-modal-close" onClick={handleClose}>
            <X size={20} />
          </button>
        </div>

        <div className="cocb-modal-body">
          {/* Drop Zone */}
          <div
            ref={dropZoneRef}
            className="cocb-drop-zone"
            onDrop={handleDrop}
            onDragOver={handleDragOver}
            onDragLeave={handleDragLeave}
            onClick={() => fileInputRef.current?.click()}
          >
            <Upload size={48} />
            <p>Drag & drop files here</p>
            <span>PDF, TIFF, JPG, PNG (max 50MB)</span>
            <input
              ref={fileInputRef}
              type="file"
              accept=".pdf,.tiff,.tif,.jpg,.jpeg,.png"
              multiple
              onChange={handleFileSelect}
              style={{ display: 'none' }}
            />
          </div>

          {/* UID Input */}
          <div className="cocb-uid-input">
            <label>UID (optional tracking ID)</label>
            <input
              type="text"
              value={uid}
              onChange={(e) => setUid(e.target.value)}
              placeholder="Optional unique identifier"
            />
          </div>

          {/* File List */}
          {files.length > 0 && (
            <div className="cocb-file-list">
              <div className="cocb-file-list-header">
                <span>{files.length} file(s) selected</span>
                {allUploaded && (
                  <span className="cocb-upload-summary">
                    {successCount} success, {errorCount} failed
                  </span>
                )}
              </div>
              <div className="cocb-file-items">
                {files.map(file => (
                  <div key={file.name} className={`cocb-file-item ${uploadStatus[file.name]}`}>
                    <div className="cocb-file-info">
                      <File size={16} />
                      <span className="cocb-file-name" title={file.name}>
                        {file.name.length > 40 ? file.name.substring(0, 40) + '...' : file.name}
                      </span>
                      <span className="cocb-file-size">
                        {(file.size / 1024 / 1024).toFixed(2)} MB
                      </span>
                    </div>
                    {uploadStatus[file.name] === 'error' && uploadResults.find(r => r.filename === file.name)?.error && (
                      <div className="cocb-file-error">
                        {uploadResults.find(r => r.filename === file.name)?.error}
                      </div>
                    )}
                    <div className="cocb-file-actions">
                      {getStatusIcon(uploadStatus[file.name])}
                      {uploadStatus[file.name] === 'pending' && !uploading && (
                        <button 
                          className="cocb-file-remove"
                          onClick={() => removeFile(file.name)}
                        >
                          <X size={14} />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="cocb-modal-footer">
          <button className="cocb-btn-cancel" onClick={handleClose}>
            {allUploaded ? 'Close' : 'Cancel'}
          </button>
          {!allUploaded && (
            <button
              className="cocb-btn-upload-all"
              onClick={handleUploadAll}
              disabled={files.length === 0 || uploading}
            >
              {uploading ? (
                <>
                  <RefreshCw size={16} className="spin" />
                  Extracting...
                </>
              ) : (
                <>
                  <Upload size={16} />
                  Extract {files.length} File{files.length !== 1 ? 's' : ''}
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

const COCBWorkOrdersList = () => {
  const navigate = useNavigate();

  const [workOrders, setWorkOrders] = useState([]);
  const [pagination, setPagination] = useState({
    total: 0, page: 1, pages: 1, limit: 10, skip: 0,
  });
  const [stats, setStats] = useState({
    total: 0, completed: 0, processing: 0, pending: 0, failed: 0,
  });
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [error, setError] = useState("");
  const [showUploadModal, setShowUploadModal] = useState(false);
  
  // Debounce timer ref for search
  const searchTimerRef = React.useRef(null);

  useEffect(() => {
    fetchData();
  }, [statusFilter]);

  const fetchData = async (skip = 0, search = searchTerm) => {
    try {
      setLoading(true);
      setError("");

      const [ordersRes, statsRes] = await Promise.all([
        cocbGetWorkOrders({
          skip,
          limit: 10,
          status: statusFilter !== "all" ? statusFilter : undefined,
          search: search || undefined,
        }),
        cocbGetStats(),
      ]);

      if (ordersRes.work_orders) {
        setWorkOrders(ordersRes.work_orders);
        setPagination(ordersRes.pagination || { 
          total: ordersRes.total || ordersRes.work_orders.length, 
          page: Math.floor(skip / 10) + 1, 
          pages: Math.ceil((ordersRes.total || ordersRes.work_orders.length) / 10), 
          limit: 10, 
          skip 
        });
      }

      if (statsRes) {
        setStats({
          total: statsRes.total_work_orders || 0,
          completed: statsRes.by_status?.completed || 0,
          processing: statsRes.by_status?.processing || 0,
          pending: statsRes.by_status?.pending || 0,
          failed: statsRes.by_status?.failed || 0,
        });
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };
  
  // Handle search with debounce
  const handleSearchChange = (e) => {
    const value = e.target.value;
    setSearchTerm(value);
    
    // Clear existing timer
    if (searchTimerRef.current) {
      clearTimeout(searchTimerRef.current);
    }
    
    // Debounce search - wait 500ms after user stops typing
    searchTimerRef.current = setTimeout(() => {
      fetchData(0, value);
    }, 500);
  };

  const handleUploadComplete = (results) => {
    fetchData(0);
    
    const successResults = results.filter(r => r.success);
    if (successResults.length === 1 && successResults[0].work_order_id) {
      setShowUploadModal(false);
      navigate(`/v1/cocb/work-order/${successResults[0].work_order_id}`);
    }
  };

  const handleDelete = async (workOrderId, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this work order?")) return;

    try {
      await cocbDeleteWorkOrder(workOrderId);
      fetchData(pagination.skip);
    } catch (err) {
      setError(`Delete failed: ${err.message}`);
    }
  };

  const handleNextPage = () => {
    if (pagination.page < pagination.pages) {
      fetchData(pagination.skip + pagination.limit);
    }
  };

  const handlePrevPage = () => {
    if (pagination.page > 1) {
      fetchData(Math.max(0, pagination.skip - pagination.limit));
    }
  };

  // No need for local filtering anymore - backend handles search
  const filteredWorkOrders = workOrders;

  const getStatusBadgeClass = (status) => {
    const classes = {
      completed: "status-completed",
      processing: "status-processing",
      pending: "status-pending",
      failed: "status-failed",
    };
    return classes[status] || "status-pending";
  };

  return (
    <div className="cocb-work-orders-list">
      {/* Header */}
      <div className="cocb-header-banner">
        <div className="cocb-header-info">
          <h2>
            <FileText size={24} />
            COCB Work Orders
          </h2>
          <p>Change of Distributor - Document Extraction</p>
        </div>
        <div className="cocb-header-actions">
          <button
            className="cocb-btn-upload"
            onClick={() => setShowUploadModal(true)}
          >
            <Upload size={18} /> Upload & Extract
          </button>
        </div>
      </div>

      {error && (
        <div className="cocb-error-banner">
          {error}
          <button onClick={() => setError("")}>×</button>
        </div>
      )}

      {/* Stats Cards */}
      <div className="cocb-stats-grid">
        <div className="cocb-stat-card">
          <div className="cocb-stat-value">{stats.total}</div>
          <div className="cocb-stat-label">Total</div>
        </div>
        <div className="cocb-stat-card completed">
          <div className="cocb-stat-value">{stats.completed}</div>
          <div className="cocb-stat-label">Completed</div>
        </div>
        <div className="cocb-stat-card processing">
          <div className="cocb-stat-value">{stats.processing}</div>
          <div className="cocb-stat-label">Processing</div>
        </div>
        <div className="cocb-stat-card failed">
          <div className="cocb-stat-value">{stats.failed}</div>
          <div className="cocb-stat-label">Failed</div>
        </div>
      </div>

      {/* Filters */}
      <div className="cocb-filters-row">
        <input
          type="text"
          placeholder="Search by ID, filename, or UID..."
          className="cocb-search-input"
          value={searchTerm}
          onChange={handleSearchChange}
        />
        <CustomStatusDropdown value={statusFilter} onChange={setStatusFilter} />
        <button className="cocb-btn-refresh" onClick={() => fetchData(0)}>
          <RefreshCw size={16} /> Refresh
        </button>
      </div>

      {/* Table */}
      <div className="cocb-table-container">
        <table className="cocb-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Filename</th>
              <th>UID</th>
              <th>Status</th>
              <th>Pages</th>
              <th>Created</th>
              <th>Duration</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan="8" className="cocb-table-loading">
                  <RefreshCw size={20} className="spin" /> Loading...
                </td>
              </tr>
            ) : filteredWorkOrders.length === 0 ? (
              <tr>
                <td colSpan="8" className="cocb-table-empty">
                  No work orders found. Upload a document to get started.
                </td>
              </tr>
            ) : (
              filteredWorkOrders.map((wo) => (
                <tr key={wo._id} onClick={() => navigate(`/v1/cocb/work-order/${wo._id}`)}>
                  <td className="cocb-id-cell">{wo._id?.slice(-8)}</td>
                  <td className="cocb-filename-cell" title={wo.filename}>
                    {wo.filename?.substring(0, 30) || "-"}
                    {wo.filename?.length > 30 && "..."}
                  </td>
                  <td className="cocb-uid-cell">{wo.uid || "-"}</td>
                  <td>
                    <span className={`cocb-status-badge ${getStatusBadgeClass(wo.status)}`}>
                      {wo.status}
                    </span>
                  </td>
                  <td>{wo.total_pages || "-"}</td>
                  <td>{formatDate(wo.created_at)}</td>
                  <td>{wo.processing_time ? `${parseFloat(wo.processing_time).toFixed(1)}s` : "-"}</td>
                  <td className="cocb-actions-cell" onClick={(e) => e.stopPropagation()}>
                    <button
                      className="cocb-action-btn view"
                      title="View Details"
                      onClick={() => navigate(`/v1/cocb/work-order/${wo._id}`)}
                    >
                      <Eye size={16} />
                    </button>
                    <button
                      className="cocb-action-btn json"
                      title="Download JSON"
                      onClick={(e) => {
                        e.stopPropagation();
                        const data = wo.extracted_data || {};
                        const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
                        const url = URL.createObjectURL(blob);
                        const a = document.createElement("a");
                        a.href = url;
                        a.download = `${wo.filename?.replace(/\.[^/.]+$/, "") || wo._id}.json`;
                        a.click();
                      }}
                    >
                      <FileJson size={16} />
                    </button>
                    <button
                      className="cocb-action-btn delete"
                      title="Delete"
                      onClick={(e) => handleDelete(wo._id, e)}
                    >
                      <Trash2 size={16} />
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      <div className="cocb-pagination">
        <div className="cocb-pagination-info">
          Showing {workOrders.length > 0 ? pagination.skip + 1 : 0} - {Math.min(pagination.skip + pagination.limit, pagination.total)} of {pagination.total}
        </div>
        <div className="cocb-pagination-controls">
          <button onClick={handlePrevPage} disabled={pagination.page <= 1}>
            <ChevronLeft size={16} /> Previous
          </button>
          <span className="cocb-page-indicator">
            Page {pagination.page} of {pagination.pages || 1}
          </span>
          <button onClick={handleNextPage} disabled={pagination.page >= pagination.pages}>
            Next <ChevronRight size={16} />
          </button>
        </div>
      </div>

      {/* Upload Modal */}
      <UploadModal
        isOpen={showUploadModal}
        onClose={() => setShowUploadModal(false)}
        onUploadComplete={handleUploadComplete}
      />
    </div>
  );
};

export default COCBWorkOrdersList;
